import type { ComponentChildren } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import { t } from '../i18n/index.js';
import { Logo } from '../components/Logo.js';
import { Footer } from '../components/Footer.js';
import { formatWhen } from './admin/ClosureAdmin.js';

function countdown(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const d = Math.floor(total / 86_400);
  const h = Math.floor((total % 86_400) / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const hms = [h, m, s].map((v) => String(v).padStart(2, '0')).join(':');
  return d > 0 ? `${d} vrk ${hms}` : hms;
}

/**
 * The page shown while the Game Arena is closed (maintenance etc., decision 2026-09-27):
 * a neon arena floor with a slowly turning gear, floating game pieces, the administrator's
 * message and a countdown when the reopening time is known.
 */
export function MaintenanceView(props: {
  title?: string;
  message: string;
  endsAt: number | null;
  clockOffset?: number;
  children?: ComponentChildren;
}) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);
  const serverNow = now + (props.clockOffset ?? 0);

  return (
    <div class="maint">
      <div class="maint-sky" aria-hidden="true">
        <div class="maint-stars" />
        <div class="maint-sun" />
        <div class="maint-grid" />
      </div>
      <div class="maint-pieces" aria-hidden="true">
        <svg class="mp mp1" viewBox="0 0 100 30">
          <path d="M8 15 C8 9 12 7 20 7 L78 7 C88 8 94 12 97 15 C94 18 88 22 78 23 L20 23 C12 23 8 21 8 15Z" fill="#cfe6ff" />
          <rect x="40" y="11" width="20" height="8" rx="2" fill="#8ea0bb" />
        </svg>
        <span class="mp mp2 disc disc-w" />
        <span class="mp mp3 disc disc-b" />
        <svg class="mp mp4" viewBox="0 0 30 40">
          <rect x="1" y="1" width="28" height="38" rx="5" fill="#5b21b6" />
          <path d="M15 12l2.4 5 5.4.8-3.9 3.8.9 5.4-4.8-2.6-4.8 2.6.9-5.4-3.9-3.8 5.4-.8z" fill="#facc15" />
        </svg>
        <svg class="mp mp5" viewBox="0 0 40 40">
          <circle cx="20" cy="20" r="16" fill="none" stroke="#5cc8ff" stroke-width="3" />
          <circle cx="20" cy="20" r="5" fill="#e5484d" />
        </svg>
      </div>

      <main class="maint-content">
        <Logo size="large" animated />
        <div class="maint-card">
          <svg class="maint-gears" viewBox="0 0 120 80" aria-hidden="true">
            <g class="gear gear-big">
              <path d={gearPath(40, 42, 26, 20, 10)} />
              <circle cx="40" cy="42" r="8" />
            </g>
            <g class="gear gear-small">
              <path d={gearPath(86, 30, 17, 12.5, 8)} />
              <circle cx="86" cy="30" r="5" />
            </g>
          </svg>
          <h1 class="maint-title">{props.title ?? t.closure.title}</h1>
          <p class="maint-message">{props.message}</p>
          {props.endsAt !== null ? (
            <div class="maint-eta">
              <span class="maint-eta-label">{t.closure.countdown}</span>
              <span class="maint-countdown">{countdown(props.endsAt - serverNow)}</span>
              <span class="muted">{t.closure.reopens(formatWhen(props.endsAt))}</span>
            </div>
          ) : (
            <p class="maint-eta muted">{t.closure.reopensUnknown}</p>
          )}
          <div class="maint-scan" aria-hidden="true" />
        </div>
        <p class="maint-hint">{t.closure.stayHint}</p>
        {props.children}
      </main>
      <Footer />
    </div>
  );
}

/** A gear outline: `teeth` teeth between the outer and inner radius. */
function gearPath(cx: number, cy: number, outer: number, inner: number, teeth: number): string {
  const points: string[] = [];
  const step = (Math.PI * 2) / teeth;
  for (let i = 0; i < teeth; i++) {
    const a = i * step;
    const corners = [
      [a - step * 0.25, inner],
      [a - step * 0.15, outer],
      [a + step * 0.15, outer],
      [a + step * 0.25, inner],
    ] as const;
    for (const [angle, r] of corners) points.push(`${(cx + Math.cos(angle) * r).toFixed(2)},${(cy + Math.sin(angle) * r).toFixed(2)}`);
  }
  return `M${points.join('L')}Z`;
}
